import { useState } from "react";
import axios from "axios";
import { Mail, Send, Sparkles } from "lucide-react";

const initialForm = {
  name: "",
  email: "",
};

function NewsletterSignup() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (status.message) setStatus({ type: "", message: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = form.email.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ type: "error", message: "Enter a valid email address." });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post("/api/newsletter/subscribe", {
        name: form.name.trim(),
        email,
        source: window.location.pathname || "website_footer",
      });

      setStatus({
        type: "success",
        message: data.message || "You're subscribed to LWBL updates.",
      });
      setForm(initialForm);
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.response?.data?.message || "Subscription failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#F7FAFA] px-4 py-14">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-lg border border-[#DCE5E5] bg-white shadow-sm">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
          <div className="bg-[#101819] p-8 text-white md:p-10">
            <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#00C3FF] text-black">
              <Mail size={22} />
            </div>
            <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#B6FCFF]">
              LWBL Newsletter
            </p>
            <h2 className="mt-3 font-cinzel text-3xl leading-tight md:text-4xl">
              Stay close to the next quest, gallery call, and community update.
            </h2>
            <p className="mt-4 max-w-xl font-outfit text-sm leading-6 text-white/72">
              Receive curated notes on current quests, member spotlights, Refine resources,
              grants, events, and practical opportunities to keep your creative practice moving.
            </p>
          </div>

          <div className="p-8 md:p-10">
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-[#DCE5E5] bg-[#F7FAFA] p-4">
              <Sparkles size={18} className="mt-0.5 flex-shrink-0 text-[#008AA0]" />
              <p className="font-outfit text-sm leading-6 text-[#536061]">
                Professional updates only. No spam, no public display of your email.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block font-outfit text-xs font-bold uppercase tracking-[0.14em] text-[#536061]">
                    Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => updateField("name", event.target.value)}
                    placeholder="Your name"
                    className="w-full rounded-lg border border-[#C9D7DA] px-4 py-3 font-outfit text-sm text-[#182124] outline-none transition focus:border-[#00C3FF] focus:ring-2 focus:ring-[#B6FCFF]"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-outfit text-xs font-bold uppercase tracking-[0.14em] text-[#536061]">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-[#C9D7DA] px-4 py-3 font-outfit text-sm text-[#182124] outline-none transition focus:border-[#00C3FF] focus:ring-2 focus:ring-[#B6FCFF]"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] px-6 py-3 font-outfit text-sm font-bold text-black shadow-sm transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                  <Send size={16} />
                </button>
                {status.message && (
                  <p
                    className={`font-outfit text-sm font-semibold ${
                      status.type === "success" ? "text-[#007D61]" : "text-red-600"
                    }`}
                  >
                    {status.message}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default NewsletterSignup;
